import { PageCategory } from '~/features/analytics/constants';
import { useClientExperiment } from '~/features/experiments/hooks/use-client-experiment';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { Page } from '~/features/page/models/page';
import { SchoolLandingPage, SchoolLandingPageProps } from '~/features/school-lp/components/school-landing-page';
import { BreadcrumbStructuredData } from '~/features/seo/components/breadcrumb-structured-data';
import { CourseListStructuredData } from '~/features/seo/components/course-list-structured-data';
import { CATALOG_BREADCRUMB, HOME_BREADCRUMB } from '~/features/seo/utils/create-breadcrumb-structured-data';
import { useScrollDepthEvent } from '~/hooks/use-scroll-depth-event';

export type SchoolLpPageProps = {
  page: Page;
  schoolName: string;
  schoolSlug: string;
} & SchoolLandingPageProps;

export function SchoolLpPage({ page, schoolName, schoolSlug, ...schoolLandingPageProps }: SchoolLpPageProps) {
  // `useExperimentalSegmentEvents` resolves the `dino` flag off `ab_plp_redesign_2025`, which is
  // never set outside `/test`/`/flag` routes, so the flag is read directly here.
  const dinoExperiment = useClientExperiment('dino');

  useScrollDepthEvent({
    threshold: [25, 50, 75, 99],
    analyticsProperties: {
      category: PageCategory.School,
    },
    isEnabled: dinoExperiment?.variant === 'on',
  });

  const recommendedPrograms = schoolLandingPageProps.staticRecommendedProgramsSectionProps?.programs ?? [];

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.School} />
      <BreadcrumbStructuredData
        crumbs={[
          HOME_BREADCRUMB,
          CATALOG_BREADCRUMB,
          { name: schoolName, url: `https://www.udacity.com/school/${schoolSlug}` },
        ]}
      />
      <CourseListStructuredData
        items={recommendedPrograms.map((program) => ({
          slug: program.slug,
          title: program.title,
          description: program.summary,
        }))}
      />
      <PageLayout withoutVerticalPadding>
        <SchoolLandingPage {...schoolLandingPageProps} />
      </PageLayout>
    </>
  );
}
